import create from 'zustand';
import { CREAT_NEW_ORDER_STOCK_STEPS } from '../types/orders';
import { IDetailProviders } from '../types/products';
import { IProductReference } from '../types/services';
import { ImoduleFilter, ISortFilter, SORT_ORDER } from '../types/global';
import { StockOrderPropertyToSearch } from '../types/stockOrders';

export interface ISelectedProductData extends IProductReference {
  quantity: number;
  unitCost: number;
  taxCost: number;
  subtotal: number;
}

export interface IState {
  showCreateNewOrderStock: boolean;
  setShowCreateNewOrderStock: (showCreateNewOrderStock: boolean) => void;
  searchWarehouse: {
    name: string;
    id: number;
    flag: string;
    currencyCode: string;
  } | null;
  setSearchWarehouse: (searchWarehouse: { name: string; id: number; flag: string; currencyCode: string } | null) => void;
  currentStep: number;
  setCurrentStep: (currentStep: number) => void;
  selectedProvider: IDetailProviders | null;
  setSelectedProvider: (selectedProvider: IDetailProviders | null) => void;
  orderProvider: string | null;
  setOrderProvider: (orderProvider: string) => void;
  invoiceProvider: string | null;
  setInvoiceProvider: (invoiceProvider: string) => void;
  selectedProducts: ISelectedProductData[];
  setSelectedProducts: (products: ISelectedProductData[]) => void;
  updateProductData: (productId: number, data: Partial<ISelectedProductData>) => void;
  removeProduct: (productId: number) => void;
  getTotalCost: () => number;
  resetStockOrdersState: () => void;
  selectedCountryCode: string | null;
  setSelectedCountryCode: (countryCode: string | null) => void;
  selectedCountryId: number | null;
  setSelectedCountryId: (countryId: number | null) => void;
  stockOrdersFilters: ImoduleFilter[];
  setStockOrdersFilters: (stockOrdersFilters: ImoduleFilter[]) => void;
  stockOrdersSortFilter: ISortFilter;
  setStockOrdersSortFilter: (sortFilter: ISortFilter) => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  propertyToFilterSearch: StockOrderPropertyToSearch | null;
  setPropertyToFilterSearch: (propertyToFilterSearch: StockOrderPropertyToSearch | null) => void;
}

const useStockOrdersModuleState = create<IState>((set, get) => ({
  currentStep: CREAT_NEW_ORDER_STOCK_STEPS.SELECT_WAREHOUSE,
  setCurrentStep: currentStep => set({ currentStep }),
  showCreateNewOrderStock: false,
  setShowCreateNewOrderStock: showCreateNewOrderStock => set({ showCreateNewOrderStock }),
  searchWarehouse: { name: '', id: 0, flag: '', currencyCode: '' },
  setSearchWarehouse: searchWarehouse => set({ searchWarehouse }),
  selectedProvider: null,
  setSelectedProvider: selectedProvider => set({ selectedProvider }),
  orderProvider: null,
  setOrderProvider: orderProvider => set({ orderProvider }),
  invoiceProvider: null,
  setInvoiceProvider: invoiceProvider => set({ invoiceProvider }),
  selectedProducts: [],
  setSelectedProducts: products => set({ selectedProducts: products }),
  updateProductData: (productId, data) =>
    set(state => ({
      selectedProducts: state.selectedProducts.map(product => {
        if (product.id === productId) {
          const quantity = data.quantity ?? product.quantity;
          const taxCost = data.taxCost ?? product.taxCost;
          const subtotal = quantity * taxCost;

          return {
            ...product,
            ...data,
            subtotal
          };
        }
        return product;
      })
    })),
  removeProduct: productId =>
    set(state => ({
      selectedProducts: state.selectedProducts.filter(product => product.id !== productId)
    })),
  getTotalCost: () => {
    const { selectedProducts } = get();
    return selectedProducts.reduce((total, product) => total + product.subtotal, 0);
  },
  selectedCountryCode: null,
  setSelectedCountryCode: countryCode => set({ selectedCountryCode: countryCode }),
  selectedCountryId: null,
  setSelectedCountryId: countryId => set({ selectedCountryId: countryId }),
  stockOrdersFilters: [],
  setStockOrdersFilters: (stockOrdersFilters: ImoduleFilter[]) =>
    set({
      stockOrdersFilters
    }),
  stockOrdersSortFilter: {
    value: SORT_ORDER.ASC
  },
  setStockOrdersSortFilter: (sortFilter: ISortFilter) => set({ stockOrdersSortFilter: sortFilter }),
  searchText: '',
  setSearchText: (searchText: string) => set({ searchText }),
  propertyToFilterSearch: null,
  setPropertyToFilterSearch: (propertyToFilterSearch: StockOrderPropertyToSearch | null) => set({ propertyToFilterSearch }),
  resetStockOrdersState: () => {
    set({
      currentStep: CREAT_NEW_ORDER_STOCK_STEPS.SELECT_WAREHOUSE,
      selectedProvider: null,
      orderProvider: null,
      invoiceProvider: null,
      selectedProducts: [],
      selectedCountryCode: null,
      selectedCountryId: null,
      stockOrdersFilters: [],
      searchWarehouse: { name: '', id: 0, flag: '', currencyCode: '' },
      searchText: '',
      propertyToFilterSearch: null,
    });
  }
}));

export default useStockOrdersModuleState;
